import { useState, useEffect, FC, useCallback, Fragment } from 'react';
import { TreeSelect } from 'primereact/treeselect';
import type {
  TreeSelectChangeEvent,
  TreeSelectEventNodeEvent,
  TreeSelectExpandedEvent,
  TreeSelectSelectionKeysType,
} from 'primereact/treeselect';
import type { TreeNode } from 'primereact/treenode';
import type { Nullable } from 'primereact/ts-helpers';
import type { TreeNodeTemplateOptions } from 'primereact/tree';
import { isArray } from 'lodash';
import { Chip, CircularProgress, Icon, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { Tooltip } from 'primereact/tooltip';
import './TreeSelect.css';

interface NodeKey {
  [key: string]: boolean;
}

type PropsSelectTree = {
  disabled?: boolean;
  name: string;
  onChange: (value: any) => void;
  value?: any;
  funcPromise: () => Promise<any>;
  funcFindId: (id: string) => Promise<any>;
  placeholder?: string;
  onCallback?: (data: any) => void;
  isLabel?: boolean;
  style?: React.CSSProperties;
  unit?: string;
  labelField?: string; // Tên field để lấy label (mặc định: 'name')
  onlySelectLeaf?: boolean; // Chỉ cho phép chọn node con (không có children)
  selectionMode?: 'single' | 'multiple' | 'checkbox'; // Cho phép chọn nhiều node
  display?: 'chip' | 'comma'; // Hiển thị giá trị (mặc định: 'comma')
};
type Initial_Type = {
  name: string;
  code: string;
};

const SelectTree: FC<PropsSelectTree> = ({
  funcPromise,
  funcFindId,
  placeholder,
  onChange,
  onCallback,
  value,
  isLabel,
  style,
  unit,
  labelField = 'name', // Mặc định là 'name'
  onlySelectLeaf = false, // Mặc định cho phép chọn cả node cha
  selectionMode = 'single', // Mặc định cho phép chọn nhiều node
  display = 'comma'
}) => {
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [selectedNodeKey, setSelectedNodeKey] =
    useState<Nullable<string | TreeSelectSelectionKeysType | TreeSelectSelectionKeysType[]>>('');
  const [expandedKeys, setExpandedKeys] = useState<NodeKey>({});
  const [label, setLabel] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const getValueOption = useCallback(
    async (id: string) => {
      try {
        const option = await funcFindId(id);
        setLabel(option);
      } catch (error) { }
    },
    [funcFindId],
  );

  useEffect(() => {
    if (value) {
      if (isLabel) {
        getValueOption(value);
      } else {
        setSelectedNodeKey(value);
      }
    }
  }, [value, getValueOption, isLabel]);

  const expandAll = (nodes: any) => {
    let _expandedKeys = {};
    if (nodes) {
      for (let node of nodes) {
        expandNode(node, _expandedKeys);
      }
      setExpandedKeys(_expandedKeys);
    }
  };

  // Không tự động expand all để tránh lag với data lớn (500+ items)
  // User có thể click để expand từng node khi cần
  // useEffect(() => {
  //   nodes && expandAll(nodes);
  // }, [nodes]);

  const collapseAll = () => {
    setExpandedKeys({});
  };

  const expandNode = (node, _expandedKeys) => {
    if (node.children && node.children.length) {
      _expandedKeys[node.key] = true;

      for (let child of node.children) {
        expandNode(child, _expandedKeys);
      }
    }
  };
  const markSelectable = (item: any) => {
    const hasChildren = item.children && item.children.length > 0;

    // Nếu onlySelectLeaf = true, chỉ cho phép chọn node không có children
    if (hasChildren) {
      return {
        ...item,
        key: item.id || item.key,
        label: item[labelField] || item.name || item.label,
        selectable: !onlySelectLeaf, // Không cho chọn nếu onlySelectLeaf = true
        children: item.children.map((child: any) => markSelectable(child)),
      };
    } else {
      return {
        ...item,
        key: item.id || item.key,
        label: item[labelField] || item.name || item.label,
        selectable: true, // Luôn cho phép chọn node con (leaf)
      };
    }
  };
  // Tìm tất cả các key của node cha (có children) để lọc khỏi giá trị trả về
  const getParentKeys = useCallback((nodes: TreeNode[]): Set<string> => {
    const parentKeys = new Set<string>();
    const findParents = (nodeList: TreeNode[]) => {
      for (const node of nodeList) {
        if (node.children && node.children.length > 0) {
          parentKeys.add(node.key as string);
          findParents(node.children);
        }
      }
    };
    findParents(nodes);
    return parentKeys;
  }, []);

  // Lọc bỏ các node cha khỏi giá trị được chọn (chỉ giữ lại leaf nodes)
  const filterLeafNodes = useCallback((
    selectedValue: TreeSelectSelectionKeysType | null,
    nodeList: TreeNode[]
  ): TreeSelectSelectionKeysType | null => {
    if (!selectedValue || typeof selectedValue !== 'object') return selectedValue;

    const parentKeys = getParentKeys(nodeList);
    const filteredValue: TreeSelectSelectionKeysType = {};

    for (const [key, value] of Object.entries(selectedValue)) {
      // Chỉ giữ lại các node không phải là parent (leaf nodes)
      if (!parentKeys.has(key)) {
        filteredValue[key] = value;
      }
    }

    return filteredValue;
  }, [getParentKeys]);

  const getTreeNodes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await funcPromise();
      const arrItem = isArray(data) ? data : data.value;
      // Áp dụng hàm markSelectable cho toàn bộ dữ liệu
      const updatedData = arrItem.map((item: any) => markSelectable(item));
      // console.log(updatedData);
      setNodes(updatedData);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getTreeNodes();
  }, []);

  return (
    <div  >
      {isLabel ? (
        label.label
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <TreeSelect
            display={display}
            selectionMode={selectionMode}
            // tooltip={get(nodes, '[0].label', undefined)}
            style={{ width: '100%', fontSize: 'inherit', position: 'relative', ...style }}
            className="tree-select-z-index"
            filter
            filterBy="label"
            filterMode="lenient"
            filterPlaceholder="Tìm kiếm..."
            filterDelay={500} // Debounce 500ms - chờ người dùng viết xong mới tìm kiếm
            name="name"
            showClear={true}
            value={selectedNodeKey}
            options={nodes}
            onChange={(e: TreeSelectChangeEvent) => {
              setSelectedNodeKey(e.value);
              // Nếu onlySelectLeaf=true, lọc bỏ các node cha khỏi giá trị trả về
              if (onlySelectLeaf && selectionMode === 'checkbox') {
                const filteredValue = filterLeafNodes(
                  e.value as TreeSelectSelectionKeysType,
                  nodes
                );
                onChange(filteredValue);
              } else {
                onChange(e.value);
              }
            }}
            onNodeSelect={(event: TreeSelectEventNodeEvent) => {
              const selectedNode = event.node;
              onCallback && onCallback(selectedNode);
            }}
            placeholder={placeholder || 'Chọn'}
            expandedKeys={expandedKeys}
            onToggle={(e: TreeSelectExpandedEvent) => setExpandedKeys(e.value)}
            // panelHeaderTemplate={headerTemplate}
            nodeTemplate={(node: TreeNode, options: TreeNodeTemplateOptions) => (
              <Fragment>
                <Icon fontSize="small" color="disabled">
                  subdirectory_arrow_right
                </Icon>
                {/* <Tooltip target={`${node.id}-custom-tooltip`} /> */}
                <div
                  data-pr-tooltip={node.label}
                  style={{ color: !node.selectable ? 'cornflowerblue' : 'inherit' }}
                  className={`${node.id}-custom-tooltip over-text-tree-select`}
                >
                  {node['children@odata.count'] > 0 ? (
                    <span>
                      {node.label} -{' '}
                      <Chip label={`${node['children@odata.count']} ${unit}`} size="small" color="primary">
                        {node['children@odata.count']} {unit}
                      </Chip>
                    </span>
                  ) : (
                    <span>{node.label}</span>
                  )}
                </div>
              </Fragment>
            )}
          />
          <MuiTooltip title="Tải lại danh sách">
            <IconButton
              size="small"
              onClick={getTreeNodes}
              disabled={loading}
              sx={{
                marginLeft: '2px',
                marginRight: '3px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                padding: '6px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {loading ? <CircularProgress size={20} /> : <Refresh fontSize="small" />}
            </IconButton>
          </MuiTooltip>
        </div>
      )}
    </div>
  );
};
export default SelectTree;
